// i18n setup (i18next + react-i18next). English is the source/fallback; Spanish and
// Portuguese are shipped. The active language is persisted to the user's `language`
// preference (server) AND localStorage (so it applies instantly + before the prefs
// fetch resolves on the next load). See CLAUDE.md §55.23/§77 + the i18n section.
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import en from '../locales/en.json'
import es from '../locales/es.json'
import pt from '../locales/pt.json'

export const SUPPORTED_LANGUAGES = ['en', 'es', 'pt'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

const STORAGE_KEY = 'grafty-lang'

function normalize(lang?: string | null): Language {
  if (!lang) return 'en'
  const base = lang.toLowerCase().split('-')[0]
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? (base as Language) : 'en'
}

// Initial language: localStorage (set last time) → browser → 'en'. The server pref
// overrides this once /me/preferences resolves (see setLanguage / accessibility provider).
const initial = normalize(
  localStorage.getItem(STORAGE_KEY) ||
    (typeof navigator !== 'undefined' ? navigator.language : 'en'),
)

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    es: { translation: es },
    pt: { translation: pt },
  },
  lng: initial,
  fallbackLng: 'en',
  interpolation: { escapeValue: false }, // React already escapes
  returnEmptyString: false,
})

// setLanguage switches the active language and remembers it locally. Persisting to the
// server pref is the caller's job (LanguageSwitcher) so this stays dependency-free.
export function setLanguage(lang: string) {
  const l = normalize(lang)
  localStorage.setItem(STORAGE_KEY, l)
  if (i18n.language !== l) i18n.changeLanguage(l)
  if (typeof document !== 'undefined') document.documentElement.lang = l
}

// applyServerLanguage is called after /me/preferences loads to sync the UI to the
// user's saved choice (without writing it back to the server).
export function applyServerLanguage(lang?: string | null) {
  if (lang) setLanguage(lang)
}

document.documentElement.lang = i18n.language
export default i18n
